/**
 * Shared suggestion providers and chip builders for ScopeSearchInput.
 *
 * The customer and site-group scopes recur on every staff grid that
 * adopts the unified scope search (orders, users, …). These factories
 * own the suggestion fetching and visual identity; the consuming grid
 * supplies only its apply/remove side effects and current state.
 *
 * Contains:
 * - customerScopeProvider / siteGroupScopeProvider — popover suggestion sources
 * - customerScopeChip / siteGroupScopeChip — applied-filter chip descriptors
 */
import { ApartmentOutlined, BusinessOutlined } from '@mui/icons-material';
import React from 'react';

import { fetchCustomerScopeSuggestions } from '@sophocles/api/customer';
import { fetchSiteGroupScopeSuggestions } from '@sophocles/api/siteGroup';

const SUGGESTION_LIMIT = 5;

export function customerScopeProvider({ label = 'Customer', isActive, applyValue }) {
  return {
    key: 'customer',
    label,
    icon: <BusinessOutlined sx={{ fontSize: 18 }} />,
    isActive,
    fetchSuggestions: async query => {
      const data = await fetchCustomerScopeSuggestions({
        offset: 0,
        limit: SUGGESTION_LIMIT,
        is_prospect: false,
        text_search: `name|${query}`,
      });
      const list = data?.results ?? data ?? [];
      return list.map(c => ({ id: c.id, label: c.name, sublabel: c.description || undefined }));
    },
    applyValue,
  };
}

export function siteGroupScopeProvider({ label = 'Site Group', getCustomerId, isActive, applyValue }) {
  return {
    key: 'site_group',
    label,
    icon: <ApartmentOutlined sx={{ fontSize: 18 }} />,
    isActive,
    fetchSuggestions: async query => {
      const params = { offset: 0, limit: SUGGESTION_LIMIT, text_search: `name|${query}` };
      const customerId = getCustomerId?.();
      if (customerId) params.customer = customerId;
      const data = await fetchSiteGroupScopeSuggestions(params);
      const list = data?.results ?? data ?? [];
      return list.map(g => ({ id: g.id, label: g.name, sublabel: g.description || undefined }));
    },
    applyValue,
  };
}

export function customerScopeChip({ label = 'Customer', displayValue, onRemove }) {
  return {
    key: 'customer',
    label,
    displayValue,
    icon: <BusinessOutlined sx={{ fontSize: 14 }} />,
    onRemove,
  };
}

export function siteGroupScopeChip({ label = 'Site Group', displayValue, onRemove }) {
  return {
    key: 'site_group',
    label,
    displayValue,
    icon: <ApartmentOutlined sx={{ fontSize: 14 }} />,
    onRemove,
  };
}
